import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, SupportedLanguage } from '@demind-inc/core';

export const LANGUAGE_OPTIONS: Array<{ value: SupportedLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'ko', label: '한국어' },
  { value: 'el', label: 'Ελληνικά' },
  { value: 'ja', label: '日本語' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'zh-Hans', label: '简体中文' },
];

const normalizeCandidate = (language: string) =>
  language.trim().split(';')[0].trim().replace(/_/g, '-');

const findSupportedLanguage = (language: string): SupportedLanguage | null => {
  const normalizedLanguage = normalizeCandidate(language);
  const lowerLanguage = normalizedLanguage.toLowerCase();

  if (!normalizedLanguage) {
    return null;
  }

  const exactMatch = SUPPORTED_LANGUAGES.find(
    (supportedLanguage) => supportedLanguage.toLowerCase() === lowerLanguage
  );

  if (exactMatch) {
    return exactMatch;
  }

  if (lowerLanguage === 'zh' || lowerLanguage.startsWith('zh-')) {
    return 'zh-Hans';
  }

  const baseLanguage = lowerLanguage.split('-')[0];
  return (
    SUPPORTED_LANGUAGES.find(
      (supportedLanguage) => supportedLanguage.toLowerCase() === baseLanguage
    ) ?? null
  );
};

export const isSupportedLanguage = (language: unknown): language is SupportedLanguage => {
  return (
    typeof language === 'string' &&
    SUPPORTED_LANGUAGES.some((supportedLanguage) => supportedLanguage === language)
  );
};

export const getSupportedLanguage = (
  languageCandidates: string | readonly string[] | null | undefined
): SupportedLanguage | null => {
  const candidates =
    typeof languageCandidates === 'string'
      ? languageCandidates.split(',')
      : (languageCandidates ?? []);

  for (const candidate of candidates) {
    const supportedLanguage = findSupportedLanguage(candidate);

    if (supportedLanguage) {
      return supportedLanguage;
    }
  }

  return null;
};

export const resolveSupportedLanguage = (
  languageCandidates: string | readonly string[] | null | undefined
): SupportedLanguage => {
  return getSupportedLanguage(languageCandidates) ?? DEFAULT_LANGUAGE;
};

export const getBrowserLanguage = (): SupportedLanguage | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const browserLanguageCandidates = [...(navigator.languages ?? []), navigator.language].filter(
    Boolean
  );

  return getSupportedLanguage(browserLanguageCandidates);
};

export const resolveAppLanguage = ({
  preferredLanguage,
  browserLanguageCandidates,
}: {
  preferredLanguage?: unknown;
  browserLanguageCandidates?: string | readonly string[] | null;
}): SupportedLanguage => {
  const savedLanguage = isSupportedLanguage(preferredLanguage) ? preferredLanguage : null;
  const hasSavedPreferenceValue = Boolean(preferredLanguage);
  const browserLanguage =
    browserLanguageCandidates === undefined
      ? getBrowserLanguage()
      : getSupportedLanguage(browserLanguageCandidates);

  return (
    savedLanguage ??
    (hasSavedPreferenceValue ? DEFAULT_LANGUAGE : (browserLanguage ?? DEFAULT_LANGUAGE))
  );
};
